import React, { useEffect, useMemo, useState } from 'react';
import CreditApplicationsTable from './CreditApplicationsTable';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { getCreditApplications } from '../../../../redux/services/creditApplicationsService';
import { getSupplierBranches } from '../../../../redux/services/supplierAdditionalDetailsService';
import { getCompanyUsers } from '../../../../redux/services/authService';
import { COLORS } from '../../../../utils/colors';
import {
  capitalizeWordsInSentence,
  CreditApplication,
  InternalFormSubmission,
  isExternalFormApplicationType,
  isFormTemplateType,
} from '@dill/dill-shared/dist/src';
import {
  Link,
  PlusCircle,
  UploadSimple,
  // ── new imports for summary card icons ──
  FileText,
  PenNib,
  CheckSquare,
  Scales,
} from 'phosphor-react';
import { getCreditApplicationTemplates } from '../../../../redux/services/creditApplicationsTemplatesService';
import SendCreditApplicationLinkModal from '../CreditApplicationComponents/SendCreditApplicationLinkModal';
import AppCreditApplicationStatus from './CreditApplicationStatus';
import GetCreditApplicationLinkModal from '../CreditApplicationComponents/GetCreditApplicationLinkModal';
import SendInternalFormModal from '../InternalForms/SendInternalFormModal';
import {
  getInternalFormSubmissions,
  getInternalFormTemplates,
} from '../../../../redux/services/internalFormsService';
import {
  WithInternalFormSubmission,
  isInternalFormRow,
  toInternalFormTableRow,
} from '../InternalForms/internalFormTableRows';
import {
  DillButton,
  DillDropdownButton,
  type DillSummaryCard,
  DillDropDownFilter,
  DillAvatar,
  DillCheckbox,
} from '../../../general/Dill';
import { useNavigate } from 'react-router-dom';
import { getCreditApplicationTemplateLabel } from '../CreditApplicationTemplate/CONSTANTS';

type CreditApplicationWithHistory = CreditApplication &
  WithInternalFormSubmission & {
    applicationUpdateHistoryId: string | null;
  };

type CreditApplicationStatusFilter = 'active' | 'all' | 'archived';

const STATUS_FILTER_OPTIONS: { value: CreditApplicationStatusFilter; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'all', label: 'All' },
  { value: 'archived', label: 'Archived' },
];

const CreditApplications = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: string[] } | null>(null);
  const [searchedCreditApplications, setSearchedCreditApplications] = useState<
    CreditApplicationWithHistory[]
  >([]);
  const [openCreditApplicationModal, setOpenCreditApplicationModal] = useState(false);
  const [openGetCreditApplicationLinkModal, setOpenGetCreditApplicationLinkModal] = useState(false);
  const [openSendInternalFormModal, setOpenSendInternalFormModal] = useState(false);
  // The Internal Forms button only shows once the supplier has at least one internal form template.
  const [hasInternalForms, setHasInternalForms] = useState(false);
  // External forms are sent from the Send Link modal, so it's offered even without any credit /
  // COD application templates.
  // Listed in the table alongside the applications (see internalFormTableRows.tsx).
  const [internalFormSubmissions, setInternalFormSubmissions] = useState<
    Omit<InternalFormSubmission, 'accessToken'>[]
  >([]);

  const { creditApplications, archivedCreditApplications } = useAppSelector(
    (state) => state.creditApplications
  );
  const { creditApplicationTemplates } = useAppSelector(
    (state) => state.creditApplicationTemplates
  );
  const [statusFilter, setStatusFilter] = useState<CreditApplicationStatusFilter>('active');
  const { user, users } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { branches: supplierBranches, loadedSupplierId: loadedSupplierBranchesId } = useAppSelector(
    (state) => state.supplierBranches
  );
  useEffect(() => {
    if (currentSupplier?.id && loadedSupplierBranchesId !== currentSupplier.id) {
      dispatch(getSupplierBranches({ supplierId: currentSupplier.id }));
    }
  }, [currentSupplier?.id, loadedSupplierBranchesId, dispatch]);
  const [isArchivedCreditAppFetched, setIsArchivedCreditAppFetched] = useState<boolean>(false);

  const salesHierarchy = useMemo(() => {
    const supplier = currentSupplier;
    const userId = user?.id ?? '';
    if (!Array.isArray(user?.roles) || !user?.roles.includes('CREDIT_APP_SALES')) return null;
    const isDirector = (supplier?.salesDirectorIds ?? []).includes(userId);
    const userRegion = supplier?.regions?.find((r) => (r.rsmIds ?? []).includes(userId));
    const userBranch = supplierBranches.find((b) =>
      (b.salesRep ?? []).some((sr) => sr.id === userId)
    );
    return {
      isDirector,
      userRegion: userRegion ?? null,
      userBranch: userBranch ?? null,
      userId,
      supplier,
    };
  }, [user, currentSupplier?.id, supplierBranches]);

  const tmHierarchy = useMemo(() => {
    const userId = user?.id ?? '';
    if (!userId) return null;
    const branches = supplierBranches;
    const tmBranches = branches.filter((b) => (b.tmIds ?? []).includes(userId));
    if (tmBranches.length === 0) return null;
    return { tmBranches };
  }, [user, supplierBranches]);

  // Also re-run after sending one from this page, so the new form shows without a refresh.
  const fetchInternalFormSubmissions = () => {
    if (!currentSupplier?.id) return;
    dispatch(getInternalFormSubmissions({ supplierId: currentSupplier.id })).then((result) => {
      if (result.type.includes('fulfilled')) {
        setInternalFormSubmissions((result as any).payload?.data ?? []);
      }
    });
  };

  useEffect(() => {
    if (!currentSupplier?.id) return;
    dispatch(getCreditApplications({ supplierId: currentSupplier?.id ?? '', isArchived: false }));
    dispatch(getCreditApplicationTemplates({ supplierId: currentSupplier?.id ?? '' }));
    dispatch(getCompanyUsers({ supplierId: currentSupplier?.id }));
    setHasInternalForms(false);
    dispatch(getInternalFormTemplates({ supplierId: currentSupplier.id })).then((result) => {
      if (result.type.includes('fulfilled')) {
        setHasInternalForms(((result as any).payload?.data ?? []).length > 0);
      }
    });
    setInternalFormSubmissions([]);
    fetchInternalFormSubmissions();
  }, [currentSupplier?.id]);

  const allCreditApplications = useMemo(() => {
    const unique = Array.from(
      new Map(
        [...creditApplications, ...archivedCreditApplications].map((item) => [item.id, item])
      ).values()
    );
    return unique
      .sort((a, b) => {
        const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bTime - aTime;
      })
      .filter((creditApplication) => {
        if (statusFilter === 'active') return !creditApplication?.isArchived;
        if (statusFilter === 'archived') return !!creditApplication?.isArchived;
        return true;
      });
  }, [creditApplications, archivedCreditApplications, statusFilter]);

  useEffect(() => {
    if (!currentSupplier?.id) return;
    if (statusFilter !== 'active' && !isArchivedCreditAppFetched) {
      dispatch(getCreditApplications({ supplierId: currentSupplier?.id ?? '', isArchived: true }));
    }
    if (statusFilter !== 'active') {
      setIsArchivedCreditAppFetched(true);
    }
  }, [currentSupplier?.id, statusFilter]);

  useEffect(() => {
    if (!user?.id) return;
    const savedFilters = localStorage.getItem(`creditApplicationsPageFilters_${user.id}`);

    if (savedFilters) {
      const parsed = JSON.parse(savedFilters);

      const hasActiveFilters = Object.values(parsed).some(
        (arr) => Array.isArray(arr) && arr.length > 0
      );

      if (hasActiveFilters) {
        setSelectedFilters(parsed);
      }
    }
  }, [user]);

  useEffect(() => {
    if (!user?.id) return;
    if (!selectedFilters || Object.keys(selectedFilters).length === 0) return;
    localStorage.setItem(
      `creditApplicationsPageFilters_${user.id}`,
      JSON.stringify(selectedFilters)
    );
  }, [selectedFilters, user]);

  useEffect(() => {
    const isCreditAppSales = user?.roles?.includes('CREDIT_APP_SALES') ?? false;
    const selected = selectedFilters ?? {};
    const creditApplicationsCopy = allCreditApplications.map((creditApplication) => {
      if (
        creditApplication.applicationUpdateHistory &&
        creditApplication.applicationUpdateHistory.length
      ) {
        return {
          ...creditApplication.applicationUpdateHistory[0],
          id: creditApplication.id,
          applicationUpdateHistoryId: creditApplication.applicationUpdateHistory[0].id,
          applicationUpdateHistory: creditApplication.applicationUpdateHistory,
          ballInCourtId: creditApplication.ballInCourtId,
          // applicationUpdateHistory[0] often has branchId/assignedSalesRep unset
          // (only AssignBranchModal sets them, and only on the history entry) —
          // fall back to the top-level record so the filters don't drop the row.
          branchId:
            creditApplication.applicationUpdateHistory[0].branchId ?? creditApplication.branchId,
          assignedSalesRep:
            creditApplication.applicationUpdateHistory[0].assignedSalesRep ??
            creditApplication.assignedSalesRep,
        };
      }
      return {
        ...creditApplication,
        applicationUpdateHistoryId: null,
      };
    });
    // Internal forms are never archived, so they only show under Active and All.
    const internalFormRows =
      statusFilter === 'archived'
        ? []
        : internalFormSubmissions.map((submission) =>
            toInternalFormTableRow<CreditApplicationWithHistory>(submission)
          );

    const isSalesUser = user?.roles?.includes('CREDIT_APP_SALES');
    const userEmail = user?.email?.toLowerCase() ?? '';

    // Newest first across both — allCreditApplications is already sorted, but the internal form
    // rows are merged in after it.
    const createdAtTime = (row: { createdAt?: Date | string | null }) =>
      row.createdAt ? new Date(row.createdAt).getTime() : 0;
    const mergedRows = [...creditApplicationsCopy, ...internalFormRows].sort(
      (a, b) => createdAtTime(b) - createdAtTime(a)
    );

    const filteredCreditApp = mergedRows.filter((app) => {
      // Whoever sent an internal form, or was asked to fill it in, can always see it.
      const submission = (app as WithInternalFormSubmission).internalFormSubmission;
      if (
        submission &&
        (submission.sentByUserId === user?.id ||
          (!!userEmail && submission.recipientEmails.includes(userEmail)))
      ) {
        return true;
      }
      if (tmHierarchy && isSalesUser) {
        return tmHierarchy.tmBranches.some((branch) => branch.branchId === app.branchId);
      }
      if (!salesHierarchy) {
        return true;
      }

      const { isDirector, userRegion, userBranch, userId, supplier } = salesHierarchy;

      // Director can access all
      if (isDirector && isSalesUser) {
        return true;
      }

      // Region access
      if (userRegion && isSalesUser) {
        return (userRegion.branchIds ?? []).includes(app.branchId ?? '');
      }

      // Branch access
      if (isSalesUser) {
        const filterEachReviewer = supplier?.creditApplicationSetting?.filterEachReviewer;

        if (filterEachReviewer) {
          return (app.assignedSalesRep ?? []).includes(userId);
        }
      }

      return true;
    });

    const hasSelections = Object.values(selected).some((arr) => (arr?.length ?? 0) > 0);
    if (!hasSelections) {
      setSearchedCreditApplications(filteredCreditApp);
      return;
    }

    const filtered = filteredCreditApp.filter((application) => {
      return Object.entries(selected).every(([key, values]) => {
        if (!values || values.length === 0) return true;

        const appValueRaw = (application as Record<string, unknown>)[key];
        if (!appValueRaw) return false;

        const appValue = String(appValueRaw).toLowerCase().replace(/_/g, ' ');
        const lowercasedValues = values.map((v: string) => v.toLowerCase().replace(/_/g, ' '));

        return lowercasedValues.includes(appValue);
      });
    });

    setSearchedCreditApplications(filtered);
  }, [
    selectedFilters,
    allCreditApplications,
    internalFormSubmissions,
    statusFilter,
    salesHierarchy,
    tmHierarchy,
    user?.roles,
  ]);

  const handleSelectFilter = (selected: { [key: string]: string[] }) => {
    setSelectedFilters(selected);
  };

  const templateOptions = useMemo(() => {
    return creditApplicationTemplates
      .filter((template) => !isFormTemplateType(template.applicationType))
      .map((template) => ({
        label: getCreditApplicationTemplateLabel(template),
        value: template.id,
      }));
  }, [creditApplicationTemplates]);

  const moreFilterOptions = useMemo(() => {
    const filteredCreditApp = allCreditApplications.filter((app) => {
      if (!salesHierarchy) {
        if (tmHierarchy) {
          return tmHierarchy.tmBranches.some((b) => b.branchId === app.branchId);
        }
        return true;
      }
      const { isDirector, userRegion, userBranch, userId, supplier } = salesHierarchy;
      if (isDirector) return true;
      if (userRegion) return (userRegion.branchIds ?? []).includes(app.branchId ?? '');
      if (userBranch) {
        if (
          supplier?.creditApplicationSetting?.filterEachReviewer &&
          user?.roles?.includes('CREDIT_APP_SALES')
        ) {
          return (app.assignedSalesRep ?? []).includes(userId);
        }
        return app.branchId === userBranch.branchId;
      }
      return true;
    });
    const branchIds =
      filteredCreditApp.filter((item) => item.branchId).map((item) => item.branchId) ?? [];
    const uniqueBanchIds = Array.from(new Set(branchIds));
    const salesRepEmail =
      filteredCreditApp
        .filter((item) => item.assignedSalesRep)
        .flatMap((item) => item.assignedSalesRep) ?? [];
    const uniqueSalesRepEmails = Array.from(new Set(salesRepEmail));

    const branches = supplierBranches;

    const filterBranch = branches
      .filter((branch) => uniqueBanchIds.includes(branch.branchId))
      .map((branch) => {
        return { label: branch.branchName, value: branch.branchId };
      });
    const salesReps = Array.from(
      new Map(
        branches
          .filter((item) => item.salesRep)
          .flatMap((branch) => branch.salesRep)
          .filter((salesRep) => uniqueSalesRepEmails.includes(salesRep?.id))
          .map((salesRep) => [salesRep?.id, salesRep])
      ).values()
    ).map((salesRep) => ({
      label: salesRep?.name ?? '',
      value: salesRep?.id ?? '',
      subLabel: salesRep?.email ?? '',
    }));

    const currentStatus = allCreditApplications.map(
      (item) => item.applicationUpdateHistory?.[0]?.status ?? item.status
    );
    const uniqueStatus = Array.from(new Set(currentStatus));
    const mappedStatus = uniqueStatus.map((status) => {
      const label = status.toLowerCase().replace(/_/g, ' ');
      return {
        label: capitalizeWordsInSentence(label),
        value: status,
      };
    });

    return { branches: filterBranch, salesReps, status: mappedStatus };
  }, [allCreditApplications, salesHierarchy, tmHierarchy, user, supplierBranches]);

  // ─── Status Summary Cards ────────────────────────────────────────────────────
  // Computed from the post-filter/post-search list so the counts always match
  // what's visible in the table.
  const statusCounts = useMemo(() => {
    // The summary cards count credit applications only.
    const apps = searchedCreditApplications.filter((app) => !isInternalFormRow(app));
    return {
      draft: apps.filter((a) => a.status === 'DRAFT').length,
      awaitingSignature: apps.filter((a) => a.status === 'AWAITING_SIGNATURE').length,
      submitted: apps.filter((a) => a.status === 'SUBMITTED').length,
      internalSignature: apps.filter((a) => a.status === 'INTERNAL_SIGNATURE').length,
      readyForDecision: apps.filter((a) => a.status === 'READY_FOR_DECISION').length,
      internallyApproved: apps.filter(
        (a) => a.status === 'INTERNALLY_APPROVED' || a.status === 'INTERNALLY_APPROVED_COD'
      ).length,
    };
  }, [searchedCreditApplications]);

  const summaryCards: DillSummaryCard[] = useMemo(
    () => [
      {
        label: 'Draft',
        value: String(statusCounts.draft),
        subLabel: 'In progress',
        icon: <FileText size={14} />,
        variant: 'default',
      },
      {
        label: 'Awaiting Signature',
        value: String(statusCounts.awaitingSignature),
        subLabel: 'Pending signature',
        icon: <PenNib size={14} />,
        variant: statusCounts.awaitingSignature > 0 ? 'warning' : 'default',
      },
      {
        label: 'Submitted',
        value: String(statusCounts.submitted),
        subLabel: 'Needs review',
        icon: <CheckSquare size={14} />,
        variant: statusCounts.submitted > 0 ? 'warning' : 'default',
      },
      {
        label: 'Ready for Decision',
        value: String(statusCounts.readyForDecision),
        subLabel: 'Awaiting approval',
        icon: <Scales size={14} />,
        variant: statusCounts.readyForDecision > 0 ? 'warning' : 'default',
      },
    ],
    [statusCounts]
  );

  const createCheckboxItem = ({
    label,
    value,
    isChecked,
    renderContent,
  }: {
    label: string;
    value: string;
    isChecked: boolean;
    renderContent?: React.ReactNode;
  }) => ({
    label,
    value,
    labelInElement: (
      <div className="flex mx-1 my-1 items-center gap-2">
        <DillCheckbox isChecked={isChecked} />
        {renderContent ?? <div className="text-black text-xs">{label}</div>}
      </div>
    ),
  });

  const createUserItem = ({
    id,
    firstName,
    lastName,
    email,
    isChecked,
  }: {
    id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    isChecked: boolean;
    index: number;
  }) => {
    const name = `${firstName ?? ''} ${lastName ?? ''}`.trim();
    return createCheckboxItem({
      label: firstName ?? '',
      value: id,
      isChecked,
      renderContent: (
        <div className="flex items-center text-xs cursor-pointer gap-1">
          <DillAvatar name={name} size={32} />
          <div className="flex flex-col text-black">
            <span>{name}</span>
            <span className="text-xs text-gray-500">{email}</span>
          </div>
        </div>
      ),
    });
  };

  const isSalesRoleOnly =
    salesHierarchy !== null &&
    !salesHierarchy.isDirector &&
    !salesHierarchy.userRegion &&
    Boolean(salesHierarchy.userBranch) &&
    Boolean(currentSupplier?.creditApplicationSetting?.filterEachReviewer);

  const ballInCourtUserIds = useMemo(
    () => new Set(allCreditApplications.map((app) => app.ballInCourtId).filter(Boolean)),
    [allCreditApplications]
  );

  const filterItems = [
    {
      id: 'applicationType',
      label: 'Filter by Application type',
      value: [
        { label: 'Credit Application', value: 'CREDIT_APPLICATION' },
        { label: 'COD Application', value: 'CASH_APPLICATION' },
        ...(internalFormSubmissions.some((submission) => submission.formType !== 'EXTERNAL')
          ? [{ label: 'Internal Form', value: 'INTERNAL_FORM' }]
          : []),
        // Matches both applications made from an external form template and (older) external
        // form submissions, which the table carries with the same applicationType.
        ...(internalFormSubmissions.some((submission) => submission.formType === 'EXTERNAL') ||
        allCreditApplications.some((app) => isExternalFormApplicationType(app.applicationType))
          ? [{ label: 'External Form', value: 'EXTERNAL_FORM' }]
          : []),
      ].map((item) =>
        createCheckboxItem({
          label: item.label,
          value: item.value,
          isChecked: selectedFilters?.applicationType?.includes(item.value) ?? false,
        })
      ),
    },
    {
      id: 'status',
      label: 'Filter by Status',
      value: moreFilterOptions.status.map((item) =>
        createCheckboxItem({
          label: item.label,
          value: item.value,
          isChecked: selectedFilters?.status?.includes(item.value) ?? false,
          renderContent: (
            <AppCreditApplicationStatus status={item.value} wrapperClassName="w-full" />
          ),
        })
      ),
    },
    {
      id: 'ballInCourtId',
      label: 'Filter by Ball In Court',
      value: users
        .filter((usr) => ballInCourtUserIds.has(usr.id))
        .map((item, idx) =>
          createUserItem({
            id: item.id,
            firstName: item.firstName,
            lastName: item.lastName,
            email: item.email,
            isChecked: selectedFilters?.ballInCourtId?.includes(item.id) ?? false,
            index: idx + 4,
          })
        ),
    },
    ...(moreFilterOptions.branches.length > 0
      ? [
          {
            id: 'branchId',
            label: 'Filter by Branch',
            value: moreFilterOptions.branches.map((item) =>
              createCheckboxItem({
                label: item.label ?? '',
                value: item.value,
                isChecked: selectedFilters?.branchId?.includes(item.value) ?? false,
              })
            ),
          },
        ]
      : []),
    ...(moreFilterOptions.salesReps.length > 0 && !isSalesRoleOnly
      ? [
          {
            id: 'assignedSalesRep',
            label: 'Filter by Sales Rep',
            value: moreFilterOptions.salesReps.map((item, index) =>
              createCheckboxItem({
                label: item.label ?? '',
                value: item.value,
                isChecked: selectedFilters?.assignedSalesRep?.includes(item.value) ?? false,
                renderContent: (
                  <div className="flex items-center text-xs cursor-pointer gap-1">
                    <DillAvatar name={item.label ?? ''} size={32} idx={index + 3} />
                    <div className="flex flex-col text-black">
                      <span>{item.label}</span>
                      <span className="text-xs text-gray-500">{item.subLabel ?? ''}</span>
                    </div>
                  </div>
                ),
              })
            ),
          },
        ]
      : []),
  ];

  const handleFilterChange = (data: Record<string, { value: string }[]>) => {
    const transformed = Object.fromEntries(
      Object.entries(data).map(([key, arr]) => [key, arr.map((item) => item.value)])
    );
    handleSelectFilter(transformed);
  };

  const toolbarLeft = (
    <div className="flex gap-2">
      <div className="w-[260px]">
        <DillDropDownFilter
          placeholder="Filters"
          closeMenu={false}
          isMulti
          showSelectedItems
          multiValue={selectedFilters}
          items={filterItems}
          showMinimalDesign
          onMultiSelectChange={handleFilterChange}
          inputHeight={32}
        />
      </div>
      <DillDropdownButton
        variant="secondary"
        label={STATUS_FILTER_OPTIONS.find((option) => option.value === statusFilter)?.label}
        showChevron
        options={STATUS_FILTER_OPTIONS}
        onSelect={(option) => setStatusFilter(option.value as CreditApplicationStatusFilter)}
        height={32}
      />
    </div>
  );

  const toolbarRight = (
    <div className="flex items-center gap-2">
      <DillButton
        variant="icon-default"
        onClick={() => setOpenGetCreditApplicationLinkModal(true)}
        icon={<Link size={16} color={COLORS.GREY_500} />}
        height={36}
        width={36}
      />
      <DillButton
        variant="secondary"
        label="Upload"
        onClick={() => navigate('overview/manualUploadCreditApplication')}
        icon={<UploadSimple size={16} weight="bold" />}
        isDisabled={user?.roles?.includes('CREDIT_APP_SALES')}
        height={36}
      />
      <DillDropdownButton
        variant="secondary"
        label="Manual Add"
        options={templateOptions}
        onSelect={(option) => navigate(`manualAddCreditApplication/${option.value}`)}
        icon={<PlusCircle weight="bold" size={16} />}
        isDisabled={user?.roles?.includes('CREDIT_APP_SALES')}
        height={36}
      />
      {hasInternalForms && (
        <DillDropdownButton
          variant="secondary"
          label="Internal Forms"
          showChevron
          options={[
            { label: 'Send Internal Form', value: 'send' },
            { label: 'View Internal Forms', value: 'view' },
          ]}
          onSelect={(option) => {
            if (option.value === 'send') {
              setOpenSendInternalFormModal(true);
            } else {
              navigate('internalForms');
            }
          }}
          height={36}
        />
      )}
      {templateOptions.length > 0 && (
        <DillButton label="Send Link" onClick={() => setOpenCreditApplicationModal(true)} />
      )}
    </div>
  );

  return (
    <div className="h-full p-4 flex">
      <SendCreditApplicationLinkModal
        open={openCreditApplicationModal}
        onExternalFormSent={fetchInternalFormSubmissions}
        handleClose={() => setOpenCreditApplicationModal(false)}
      />
      <GetCreditApplicationLinkModal
        open={openGetCreditApplicationLinkModal}
        handleClose={() => setOpenGetCreditApplicationLinkModal(false)}
      />
      <SendInternalFormModal
        open={openSendInternalFormModal}
        handleClose={() => setOpenSendInternalFormModal(false)}
        onSent={fetchInternalFormSubmissions}
      />
      <CreditApplicationsTable
        creditApplications={searchedCreditApplications}
        archiveCreditAppToogle={statusFilter !== 'all'}
        summaryCards={summaryCards}
        toolbarLeft={toolbarLeft}
        toolbarRight={toolbarRight}
        onInternalFormUpdated={(updated) =>
          setInternalFormSubmissions((prev) =>
            prev.map((submission) => (submission.id === updated.id ? updated : submission))
          )
        }
        onInternalFormDeleted={(id) =>
          setInternalFormSubmissions((prev) => prev.filter((submission) => submission.id !== id))
        }
      />
    </div>
  );
};

export default CreditApplications;
